import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';
import { 
  Menu, 
  ChevronDown, 
  User, 
  LogOut, 
  LayoutDashboard, 
  Award, 
  ShoppingBag, 
  Sparkles, 
  Shield, 
  ShoppingCart,
  Search,
  Home,
  Building2,
  Users,
  FileText,
  Lock,
  RefreshCw,
  BookOpen,
  Mic,
  ChevronRight,
  ArrowRight
} from 'lucide-react';
import { IndustryMegaMenu } from '@/components/common/IndustryMegaMenu';

// Logo SVG Component with animations (transparent background)
const Logo = ({ className = "", isScrolled = false, isDark = false }) => (
  <svg className={`logo-animated ${className}`} viewBox="0 0 280 55" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Arrow/Path Symbol - with pulse animation */}
    <g className="logo-icon">
      <path
        className="logo-arrow-1"
        d="M10 27 L28 8 L46 27 L28 19 L10 27Z"
        fill={isDark || isScrolled ? "#1B4F72" : "#D4A03A"}
      />
      <path
        className="logo-arrow-2"
        d="M28 19 L46 27 L28 46 L28 36 L16 27 L28 19Z"
        fill={isDark || isScrolled ? "#D4A03A" : "#FFFFFF"}
        fillOpacity="0.9"
      />
    </g>
    {/* GRC Text */}
    <text
      x="56"
      y="35"
      fontFamily="Space Grotesk, sans-serif"
      fontSize="26"
      fontWeight="700"
      fill={isDark || isScrolled ? "#1B4F72" : "#FFFFFF"}
      className="logo-text-main"
    >
      GRC
    </text>
    <text
      x="110"
      y="35"
      fontFamily="Space Grotesk, sans-serif"
      fontSize="26"
      fontWeight="700"
      fill={isDark || isScrolled ? "#D4A03A" : "#D4A03A"}
      className="logo-text-secondary"
    >
      EDUCATORS
    </text>
    {/* Tagline */}
    <text
      x="56"
      y="50"
      fontFamily="Plus Jakarta Sans, sans-serif"
      fontSize="9"
      fontWeight="500"
      fill={isDark || isScrolled ? "#64748B" : "rgba(255,255,255,0.7)"}
      letterSpacing="0.1em"
      className="logo-tagline"
    >
      YOUR PATH TO EXCELLENCE
    </text>
  </svg>
);

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, isAuthenticated, logout } = useAuth();
  const { cartCount } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const mainNavLinks = [
    { name: 'Home', href: '/' },
    { name: 'Industries', href: '/industries' },
  ];

  const webinarDropdownLinks = [
    { name: 'Live Webinars', href: '/webinars?type=live' },
    { name: 'Recorded Webinars', href: '/webinars?type=recorded' },
    { name: 'Certified Webinars', href: '/webinars?type=certified' },
    { name: 'Webinar Bundles', href: '/bundles' },
    { name: 'Best Sellers', href: '/webinars?type=bestseller' },
  ];

  const seminarDropdownLinks = [
    { name: 'Live Seminars', href: '/seminars?type=live' },
    { name: 'Recorded Seminars', href: '/seminars?type=recorded' },
    { name: 'Certified Seminars', href: '/seminars?type=certified' },
    { name: 'Seminar Series', href: '/seminars?type=series' },
    { name: 'Best Sellers', href: '/seminars?type=bestseller' },
  ];

  const exploreLinks = [
    { name: 'About Company', href: '/about', icon: Building2 },
    { name: 'Articles', href: '/articles', icon: BookOpen },
    { name: 'Join Speaker Panel', href: '/join-speaker', icon: Mic },
    { name: 'Terms of Use', href: '/terms-of-use', icon: FileText },
    { name: 'Privacy Policy', href: '/privacy-policy', icon: Lock },
    { name: 'Refund & Cancellation', href: '/refund-policy', icon: RefreshCw },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/webinars?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-zinc-200 shadow-sm"
      data-testid="navbar"
    >
      <div className="container-custom">
        <nav className="flex items-center justify-between h-16">
          {/* Left: Logo + Nav kept close together */}
          <div className="flex items-center gap-5">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="logo-link"
          >
            <Logo 
              className="h-[52px] w-auto transition-transform duration-300 group-hover:scale-105" 
              isScrolled={isScrolled}
              isDark={true}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Home Link */}
            <Link
              to="/"
              className={`px-4 py-2.5 rounded-xl font-medium text-[15px] transition-all duration-300 relative ${
                isActive('/')
                  ? true
                    ? 'text-navy-700 bg-navy-50'
                    : 'text-white bg-white/15'
                  : true
                  ? 'text-slate-600 hover:text-navy-700 hover:bg-slate-50'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
              data-testid="nav-link-home"
            >
              Home
            </Link>

            {/* Industries — simple link (no dropdown) */}
            <Link
              to="/industries"
              className={`px-4 py-2.5 rounded-xl font-medium text-[15px] transition-all duration-300 ${
                isActive('/industries')
                  ? 'text-zinc-950 bg-zinc-100'
                  : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-50'
              }`}
              data-testid="nav-link-industries"
            >
              Industries
            </Link>

            {/* Webinars — simple link (no dropdown) */}
            <Link
              to="/webinars"
              className={`px-4 py-2.5 rounded-xl font-medium text-[15px] transition-all duration-300 ${
                location.pathname.startsWith('/webinars')
                  ? 'text-zinc-950 bg-zinc-100'
                  : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-50'
              }`}
              data-testid="nav-link-webinars"
            >
              Webinars
            </Link>

            {/* Seminars Dropdown - Hidden for now */}

            {/* Explore Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg font-medium text-sm transition-colors duration-200 ${
                    true
                      ? 'text-slate-600 hover:text-navy-700 hover:bg-slate-50'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid="explore-dropdown"
                >
                  Explore
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 p-2">
                {exploreLinks.map((link, idx) => (
                  <React.Fragment key={link.name}>
                    <DropdownMenuItem asChild>
                      <Link
                        to={link.href}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer"
                      >
                        <link.icon className="w-4 h-4 text-navy-500" />
                        <span>{link.name}</span>
                      </Link>
                    </DropdownMenuItem>
                    {idx === 3 && <DropdownMenuSeparator />}
                  </React.Fragment>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Contact Link */}
            <Link
              to="/contact"
              className={`px-4 py-2.5 rounded-xl font-medium text-[15px] transition-all duration-300 ${
                isActive('/contact')
                  ? true
                    ? 'text-navy-700 bg-navy-50'
                    : 'text-white bg-white/15'
                  : true
                  ? 'text-slate-600 hover:text-navy-700 hover:bg-slate-50'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
              data-testid="nav-link-contact"
            >
              Contact
            </Link>
          </div>
          </div>

          {/* Right Side: Search, Cart, Auth */}
          <div className="hidden lg:flex items-center gap-2">
            {/* Desktop Search Bar */}
            <form onSubmit={handleSearch} className="relative hidden lg:block mr-1" data-testid="navbar-search-form">
              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-40 h-9 pl-9 pr-3 rounded-lg bg-zinc-50 border-zinc-200 text-sm text-zinc-700 placeholder:text-zinc-400 focus:w-52 focus:bg-white transition-all duration-300"
                data-testid="navbar-search-input"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
            </form>
            {/* Cart Icon */}
            <Link to={isAuthenticated ? "/cart" : "/login?redirect=/cart"}>
              <Button
                variant="ghost"
                size="icon"
                className={`relative rounded-xl transition-all ${
                  true
                    ? 'hover:bg-slate-100 text-slate-700'
                    : 'hover:bg-white/10 text-white'
                }`}
                data-testid="cart-icon"
              >
                <ShoppingCart className="w-5 h-5" />
                {isAuthenticated && cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-gold-500 text-navy-900 text-xs font-bold rounded-full flex items-center justify-center">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </Button>
            </Link>
            
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className={`flex items-center gap-3 px-4 py-2 rounded-xl transition-all ${
                      true
                        ? 'hover:bg-slate-100'
                        : 'hover:bg-white/10'
                    }`}
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-9 h-9 rounded-lg bg-zinc-900 flex items-center justify-center text-white font-semibold text-sm">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </div>
                    <span className={`font-medium ${
                      true ? 'text-slate-700' : 'text-white'
                    }`}>
                      {user?.first_name}
                    </span>
                    <ChevronDown className={`w-4 h-4 ${
                      true ? 'text-slate-400' : 'text-white/60'
                    }`} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 p-2">
                  <div className="px-3 py-2 mb-2">
                    <p className="font-semibold text-navy-700">{user?.first_name} {user?.last_name}</p>
                    <p className="text-sm text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2 cursor-pointer">
                      <LayoutDashboard className="w-4 h-4" />
                      <span>Dashboard</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard/webinars" className="flex items-center gap-3 px-3 py-2 cursor-pointer">
                      <ShoppingBag className="w-4 h-4" />
                      <span>My Webinars</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard/certificates" className="flex items-center gap-3 px-3 py-2 cursor-pointer">
                      <Award className="w-4 h-4" />
                      <span>Certificates</span>
                    </Link>
                  </DropdownMenuItem>
                  {user?.role === 'admin' || user?.role === 'super_admin' ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-3 px-3 py-2 cursor-pointer text-gold-600">
                          <Shield className="w-4 h-4" />
                          <span>Admin Panel</span>
                        </Link>
                      </DropdownMenuItem>
                    </>
                  ) : null}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer text-red-600"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-lg font-medium text-sm text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                    data-testid="login-btn"
                  >
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    size="sm"
                    className="bg-zinc-950 hover:bg-zinc-800 text-white font-semibold text-sm rounded-lg px-4 transition-colors duration-200"
                    data-testid="register-btn"
                  >
                    Get Started
                    <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={`lg:hidden rounded-xl ${
                  true
                    ? 'text-slate-700 hover:bg-slate-100'
                    : 'text-white hover:bg-white/10'
                }`}
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] p-0">
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="p-6 border-b border-slate-100">
                  <Logo className="h-12 w-auto" isScrolled={true} isDark={true} />
                </div>

                {/* Search Bar */}
                <div className="p-4 border-b border-slate-100">
                  <form onSubmit={handleSearch}>
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="Search by title, code, speaker, topic..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 rounded-xl"
                      />
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    </div>
                  </form>
                </div>

                {/* Nav Links */}
                <div className="flex-1 overflow-y-auto py-4">
                  <div className="space-y-1 px-4">
                    {mainNavLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
                          isActive(link.href)
                            ? 'bg-navy-50 text-navy-700'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-navy-700'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                    
                    <Link
                      to="/contact"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
                        isActive('/contact')
                          ? 'bg-navy-50 text-navy-700'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-navy-700'
                      }`}
                    >
                      Contact
                    </Link>
                  </div>

                  {/* Explore Section */}
                  <div className="mt-4 px-4">
                    <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">Explore</p>
                    <div className="space-y-1">
                      {exploreLinks.map((link) => (
                        <Link
                          key={link.name}
                          to={link.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 hover:text-navy-700 transition-all"
                        >
                          <link.icon className="w-4 h-4 text-navy-400" />
                          {link.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer with Auth */}
                <div className="p-6 border-t border-slate-100 bg-slate-50">
                  {isAuthenticated ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 p-4 bg-white rounded-xl">
                        <div className="w-12 h-12 rounded-lg bg-zinc-900 flex items-center justify-center text-white font-bold">
                          {user?.first_name?.[0]}{user?.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-semibold text-navy-700">{user?.first_name} {user?.last_name}</p>
                          <p className="text-sm text-slate-500">{user?.email}</p>
                        </div>
                      </div>
                      <Link
                        to="/cart"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl text-navy-700 hover:bg-navy-50 transition-colors"
                      >
                        <ShoppingCart className="w-5 h-5" />
                        Cart
                        {cartCount > 0 && (
                          <span className="ml-auto bg-gold-500 text-navy-900 text-xs font-bold rounded-full px-2 py-0.5">
                            {cartCount}
                          </span>
                        )}
                      </Link>
                      <Link
                        to="/dashboard"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl text-navy-700 hover:bg-navy-50 transition-colors"
                      >
                        <LayoutDashboard className="w-5 h-5" />
                        Dashboard
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout();
                          setMobileMenuOpen(false);
                        }}
                        className="flex items-center gap-3 px-4 py-3 w-full bg-red-50 rounded-xl text-red-600 hover:bg-red-100 transition-colors"
                      >
                        <LogOut className="w-5 h-5" />
                        Logout
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Link
                        to="/login"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-slate-200 rounded-xl text-navy-700 font-medium hover:bg-slate-50 transition-colors"
                      >
                        Sign In
                      </Link>
                      <Link
                        to="/register"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-zinc-900 rounded-md text-white font-semibold hover:bg-zinc-800 transition-colors"
                      >
                        <Sparkles className="w-4 h-4" />
                        Get Started
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
